import { cn } from "@cronika/ui";
import { ReactNode } from "react";

export type TSegment<TValue extends string> = {
  value: TValue;
  label?: ReactNode;
  icon?: ReactNode;
  "aria-label"?: string;
};

type TSegmentedToggleProps<TValue extends string> = {
  segments: TSegment<TValue>[];
  value: TValue;
  onChange: (value: TValue) => void;
  className?: string;
  size?: "sm" | "md";
};

/**
 * Pill-style segmented control for the Duration/Time mode switch. The track is a
 * 28px-tall rounded-full plate with a hairline border; the active segment fills
 * with an accent radial gradient (per Figma).
 */
export function SegmentedToggle<TValue extends string>({
  segments,
  value,
  onChange,
  className,
  size = "md",
}: TSegmentedToggleProps<TValue>) {
  return (
    <div
      className={cn(
        "inline-flex items-center gap-0.5 rounded-[12px] border border-default-500/15 bg-glass/4 p-0.5",
        className,
      )}
    >
      {segments.map((segment) => {
        const active = segment.value === value;
        return (
          <button
            key={segment.value}
            type="button"
            aria-label={segment["aria-label"]}
            aria-pressed={active}
            onClick={() => onChange(segment.value)}
            className={cn(
              "inline-flex items-center justify-center gap-1 rounded-[9px] font-medium leading-none transition-colors cursor-pointer [&>svg]:block",
              size === "sm" ? "h-[22px] w-[30px] text-xs" : "h-[22px] w-[30px] text-sm",
              active
                ? "text-black [background:var(--gradient-accent-toggle)]"
                : "text-default-500 hover:text-white",
            )}
          >
            {segment.icon}
            {segment.label}
          </button>
        );
      })}
    </div>
  );
}
